import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShoppingCart, Loader2, Check, AlertTriangle } from "lucide-react";

/**
 * AmazonBulk — paste a list of Amazon URLs and get affiliate product entries
 * created in one click. Best-effort scrape of title/image/price via the
 * `/api/admin/products/amazon-bulk` endpoint. Any URL that can't be scraped
 * still gets created with the ASIN so the admin can hand-fill it later.
 */
export default function AmazonBulk() {
  const [urls, setUrls] = useState("");
  const [category, setCategory] = useState("Books");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!urls.trim()) {
      toast.error("Paste at least one Amazon URL first.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/admin/products/amazon-bulk", { urls, default_category: category });
      setResult(res.data);
      const c = res.data?.created?.length || 0;
      const f = res.data?.failed?.length || 0;
      if (c) toast.success(`Imported ${c} product${c === 1 ? "" : "s"} from Amazon.`);
      if (f && !c) toast.error(`All ${f} URLs failed to import.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amazon import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="amazon-bulk-import"
      className="border border-[#febd69]/50 rounded-2xl bg-gradient-to-br from-[#fff8e7] via-white to-white p-6 md:p-8"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 grid place-items-center bg-[#febd69] rounded-full flex-shrink-0">
          <ShoppingCart className="w-5 h-5 text-[#232F3E]" />
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl leading-snug">Amazon Bulk Import</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Paste one Amazon URL per line (or comma-separated). Each product is scraped for its title, image and price, then saved as an affiliate item — ready to appear across the site. Add up to <b>50 URLs at a time</b>.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="overline text-muted-foreground block mb-2">Amazon URLs</label>
          <textarea
            data-testid="amazon-bulk-urls"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={8}
            placeholder={"https://www.amazon.fr/dp/B08N5WRWNW?tag=yourtag-21\nhttps://www.amazon.com/dp/1524762741?tag=yourtag-20\nhttps://amzn.to/3xYzAbC"}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary font-mono text-xs"
          />
          <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-primary" />
            Include your <code className="bg-slate-100 px-1 rounded">tag=</code> parameter in each URL so commissions get credited.
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="overline text-muted-foreground block mb-2">Default Category</label>
            <select
              data-testid="amazon-bulk-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary text-sm"
            >
              {["Books", "Website Resources", "Marketing Resources", "Tech Gear", "Home Office", "Language Learning", "Accessories"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="amazon-bulk-import-btn"
            type="button"
            disabled={busy}
            onClick={run}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#febd69] hover:bg-[#f3a847] disabled:opacity-60 text-[#111] font-semibold px-4 py-3 rounded-full transition-colors"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Fetching from Amazon…</> : <>Import URLs</>}
          </button>
        </div>
      </div>

      {result && (
        <div data-testid="amazon-bulk-result" className="mt-6 space-y-3">
          {result.created?.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
              <p className="font-medium text-emerald-800 flex items-center gap-2 mb-2">
                <Check className="w-4 h-4" /> {result.created.length} imported
              </p>
              <div className="space-y-1 text-xs text-emerald-900">
                {result.created.slice(0, 20).map((c) => (
                  <div key={c.id} className="flex items-center gap-2 truncate">
                    {c.image && <img src={c.image} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />}
                    <span className="truncate flex-1">{c.name}</span>
                    <span className="font-mono text-[10px] text-emerald-700 flex-shrink-0">
                      {c.asin} · {c.scraped ? "scraped" : "asin-only"}{c.price ? ` · ${c.price}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.failed?.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
              <p className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> {result.failed.length} failed
              </p>
              <div className="space-y-1 text-xs text-amber-900 font-mono">
                {result.failed.slice(0, 20).map((f, i) => (
                  <div key={i} className="truncate">{f.url} — {f.error}</div>
                ))}
              </div>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Note: Amazon actively blocks bots, so a couple of scrapes may miss the price or image on the first pass. Edit those items in the Affiliate Links table below to fill in the gaps — the affiliate link itself is already saved and clickable.
          </p>
        </div>
      )}
    </section>
  );
}
